import ConfirmModal from '@cashew/confirm-modal';
import Pagination from '@cashew/pagination';
import type { order } from '@cashew/types';
import cn from 'classnames';
import moment from 'moment';
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Input,
  List,
  PageWithNavigation,
  Tabs,
  Title,
} from '../../components';
import SettlementItem from '../../components/SettlementItem';
import './index.css';
import Excel from '../../assets/Excel';
import { setSelectedMerchants } from '../../reducers/general';
import OrdersFilter, { getOrdersApi } from './OrdersFilter';

const { api } = require('@cashew/utils');

const ns = 'settlements';

const Container = ({
  key,
  list,
  records,
  pageSize,
  pageNumber,
  setPageNumber,
  setPageSize,
  t,
  isLoading,
  tabIndex,
}: any) => (
  <div key={key}>
    <div className={`${ns}__headers`}>
      <p />
      <p className="flex justify-content-center align-items-center">
        {t('settlements.table.column.id')}
      </p>
      <p className="flex justify-content-center align-items-center flex-column">
        <div>
          {t('settlements.table.column.startDate')}-
          {t('settlements.table.column.endDate')}
        </div>
        {tabIndex !== 1 && (
          <div>({t('settlements.table.column.processedDate')})</div>
        )}
      </p>
      <p className="flex justify-content-ceter align-items-center flex-column">
        <div className="flex justify-content-center align-items-center">
          {t('settlements.table.column.companyId')}
        </div>
        <div className="flex justify-content-center align-items-center">
          {t('settlements.table.column.companyName')}
        </div>
      </p>
      <p className="justify-content-end flex-column align-items-center flex">
        <div>{t('settlements.table.column.amount')}</div>
        <div>{t('settlements.table.column.status')}</div>
      </p>
      <p />
    </div>
    <List data={list} component={SettlementItem} isLoading={isLoading} />
    <Pagination
      records={records}
      pageSize={pageSize}
      pageNumber={pageNumber}
      setPageNumber={setPageNumber}
      changePageSize={setPageSize}
    />
  </div>
);

/**
 * Settlements Page RefundPage
 */
export default () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [list, setList] = useState<Array<order.OrderType> | undefined>(
    undefined
  );
  const [tabIndex, setTabIndex] = useState(0);
  const [records, setRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);
  const [startDate, setStartDate] = useState(
    moment().subtract(2, 'years').format('YYYY-MM-DD')
  );
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [isProcessingSummaryReport, setIsProcessingSummaryReport] =
    useState(false);
  const [isProcessingDetailsReport, setIsProcessingDetailsReport] =
    useState(false);
  const [dateToError, setDateToError] = useState('');
  const [dateFromError, setDateFromError] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('PENDING,UNDER_REVIEW,PROCESSED');
  const [params, setParams] = useState({});
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { selectedMerchants } = useSelector((state: any) => state.general);
  const { token } = useSelector((state: any) => state.user);
  const [searchId, setSearchId] = useState('');
  const [searchBy, setSearchBy] = useState<string>('merchantName');

  const reFetchOrders = async () => {
    await getOrdersApi(true, {
      setLoading: setIsLoading,
      setRecords,
      setList,
      pageSize,
      pageNumber,
      dispatch,
      setPageNumber,
      type: 'cashew',
      records,
      startDate,
      endDate,
      tabIndex,
      status,
      setStatus,
      setParams,
      token,
      searchId,
      searchBy,
      t,
    });
  };

  const handleMarkAsProcessed = async () => {
    try {
      await api.patch({
        endpoint: 'settlements/batch/status',
        params: selectedMerchants,
      });
      dispatch({
        type: 'OPEN_TOASTER',
        payload: {
          toasterTitle: 'Success',
          toasterMessage: 'Selected settlements have been marked as processed.',
          toasterType: 'success',
          toasterVisible: true,
          toasterDismiss: null,
        },
      });
      setPageNumber(pageNumber);
      dispatch(setSelectedMerchants([]));
      await reFetchOrders();
    } catch (error) {
      console.error('Failed to mark settlements as processed:', error);
      dispatch({
        type: 'OPEN_TOASTER',
        payload: {
          toasterTitle: 'Error',
          toasterMessage:
            'Failed to mark settlements as processed. Please try again.',
          toasterType: 'error',
          toasterVisible: true,
          toasterDismiss: null,
        },
      });
      dispatch(setSelectedMerchants([]));
      await reFetchOrders();
    } finally {
      setShowConfirmModal(false);
    }
  };

  useEffect(() => {
    history.push('merchant-settlements');
  }, []);

  return (
    <div className={ns}>
      <Helmet>
        <title>{t('settlements.merchant.sectionTitle')}</title>
      </Helmet>
      <PageWithNavigation>
        <Title>
          <span>{t('settlements.merchant.title')}</span>
          <span>
            <Button
              extraClass="blue show-filter-btn"
              onClick={() => setShowFilter(!showFilter)}
            >
              <div className="btn-with-icon">
                <img src="/fillter.svg" alt="filter" />
                Filters
              </div>
            </Button>
          </span>
        </Title>
        <div className={cn({ 'hide-filter': !showFilter })}>
          <div className="filters-bar">
            <div className="filters">
              <div className="filter-group gap-2 gap-md-1 w-full flex min-h-[40px] flex-row flex-wrap justify-content-start align-items-end">
                <OrdersFilter
                  setLoading={setIsLoading}
                  page="orders"
                  type="cashew"
                  setRecords={setRecords}
                  setList={setList}
                  pageSize={pageSize}
                  pageNumber={pageNumber}
                  history={history}
                  dispatch={dispatch}
                  setPageNumber={setPageNumber}
                  records={records}
                  startDate={startDate}
                  endDate={endDate}
                  tabIndex={tabIndex}
                  status={status}
                  setStatus={setStatus}
                  setParams={setParams}
                />
                <div className="filter-input w-full lg:w-auto ">
                  <label>From</label>
                  <Input
                    name="startDate"
                    value={startDate}
                    type="date"
                    error={dateFromError ? ' ' : ''}
                    onChange={(e: any) => {
                      setDateToError('');
                      setStartDate(e.currentTarget.value);

                      if (
                        moment(e.currentTarget.value).isAfter(endDate) &&
                        endDate
                      ) {
                        setDateFromError(t('validation-less'));
                      } else {
                        setDateFromError('');
                      }
                    }}
                  />
                  <span className="error-message">{dateFromError}</span>
                </div>
                <div className="filter-input w-full lg:w-auto ">
                  <span>To</span>
                  <Input
                    name="endDate"
                    value={endDate}
                    type="date"
                    error={dateToError ? ' ' : ''}
                    onChange={(e: any) => {
                      setDateFromError('');
                      setEndDate(e.currentTarget.value);
                      if (
                        moment(e.currentTarget.value).isBefore(startDate) &&
                        startDate
                      ) {
                        setDateToError(t('validation-greater'));
                      } else {
                        setDateToError('');
                      }
                    }}
                  />
                  <span className="error-message">{dateToError}</span>
                </div>

                <div className="flex gap-2 justify-content-start flex-row mt-2">
                  <button
                    type="button"
                    className="rounded-2 inline-flex items-center h-[40px] flex justify-content-center item-center px-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-black hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-30 disabled:cursor-not-allowed"
                    onClick={() => setShowConfirmModal(true)}
                    disabled={
                      !selectedMerchants || selectedMerchants.length === 0
                    }
                  >
                    Mark selection as processed
                  </button>

                  <div className="relative flex justify-content-center align-items-center text-left">
                    <button
                      type="button"
                      className="flex justify-center w-full rounded-md border border-black rounded-2 shadow-sm h-[40px] flex justify-content-center items-center px-3  bg-white text-sm font-medium text-black hover:bg-black-700 focus:outline-none"
                      id="export-menu-button"
                      aria-expanded="true"
                      aria-haspopup="true"
                      onClick={() => setShowExportDropdown((prev) => !prev)}
                    >
                      Export to excel
                      <span className="flex items-center justify-content-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          className="size-5 mx-1"
                        >
                          <title>Export Icon</title>
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="m19.5 8.25-7.5 7.5-7.5-7.5"
                          />
                        </svg>
                      </span>
                    </button>
                    {showExportDropdown && (
                      <div className="origin-top-right absolute right-0 mt-[150px] w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                        <div
                          className="py-2"
                          role="menu"
                          aria-orientation="vertical"
                          aria-labelledby="export-menu-button"
                        >
                          <button
                            type="button"
                            className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            role="menuitem"
                            disabled={isProcessingSummaryReport}
                            onClick={async (e) => {
                              setShowExportDropdown(false);
                              setIsProcessingSummaryReport(true);
                              await api
                                .get({
                                  endpoint: 'settlements/excel-summary',
                                  params,
                                })
                                .then((response: any) => {
                                  const a = document.createElement('a');
                                  a.href = response.data.downloadUrl;
                                  document.body.appendChild(a);
                                  a.click();
                                })
                                .catch((error: any) => {
                                  console.log(error);
                                  dispatch({
                                    type: 'OPEN_TOASTER',
                                    payload: {
                                      toasterTitle:
                                        'Error while generating report',
                                      toasterMessage:
                                        'Something went wrong while generating report',
                                      toasterType: 'error',
                                      toasterVisible: true,
                                      toasterDismiss: null,
                                    },
                                  });
                                });
                              setIsProcessingSummaryReport(false);
                            }}
                          >
                            <Excel className="w-4 inline-block mr-2" />
                            {isProcessingSummaryReport
                              ? `${t('settlements.sectionFilter.exportSummary')}...`
                              : t('settlements.sectionFilter.exportSummary')}
                          </button>
                          <button
                            type="button"
                            className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            role="menuitem"
                            disabled={isProcessingDetailsReport}
                            onClick={async (e) => {
                              setShowExportDropdown(false);
                              setIsProcessingDetailsReport(true);
                              await api
                                .get({
                                  endpoint: 'settlements/excel-details',
                                  params,
                                })
                                .then((response: any) => {
                                  const a = document.createElement('a');
                                  a.href = response.data.downloadUrl;
                                  document.body.appendChild(a);
                                  a.click();
                                })
                                .catch((error: any) => {
                                  console.log(error);
                                  dispatch({
                                    type: 'OPEN_TOASTER',
                                    payload: {
                                      toasterTitle:
                                        'Error while generating report',
                                      toasterMessage:
                                        'Something went wrong while generating report',
                                      toasterType: 'error',
                                      toasterVisible: true,
                                      toasterDismiss: null,
                                    },
                                  });
                                });
                              setIsProcessingDetailsReport(false);
                            }}
                          >
                            <Excel className="w-4 inline-block mr-2" />
                            {isProcessingDetailsReport
                              ? `${t('settlements.sectionFilter.exportDetails')}...`
                              : t('settlements.sectionFilter.exportDetails')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              
              </div>
             
             
            </div>
          </div>
        </div>
 
        <div className="space-div" />

        <Box withHeader>
          <Tabs
            headers={[
              t('settlements.tab.all'),
              t('settlements.tab.pending'),
              t('settlements.tab.underReview'),
              t('settlements.tab.processed'),
            ]}
            active={tabIndex}
            changeTab={(index: number) => {
              setTabIndex(index);
            }}
          >
            <Container
              key={0}
              list={list}
              records={records}
              pageSize={pageSize}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              setPageSize={setPageSize}
              ns={ns}
              t={t}
              isLoading={isLoading}
              tabIndex={tabIndex}
            />
            <Container
              key={1}
              list={list}
              records={records}
              pageSize={pageSize}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              setPageSize={setPageSize}
              ns={ns}
              t={t}
              isLoading={isLoading}
              tabIndex={tabIndex}
            />
            <Container
              key={2}
              list={list}
              records={records}
              pageSize={pageSize}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              setPageSize={setPageSize}
              ns={ns}
              t={t}
              isLoading={isLoading}
              tabIndex={tabIndex}
            />
            <Container
              key={3}
              list={list}
              records={records}
              pageSize={pageSize}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              setPageSize={setPageSize}
              ns={ns}
              t={t}
              isLoading={isLoading}
              tabIndex={tabIndex}
            />
          </Tabs>
        </Box>
      </PageWithNavigation>
      <ConfirmModal
        isVisible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        confirmTitle="Mark settlement"
        confirmDescription="Are you sure you want to mark as processed?"
        confirmButtons={
          <>
            <Button
              extraClass="secondary"
              onClick={() => setShowConfirmModal(false)}
            >
              No
            </Button>
            <Button extraClass="blue" onClick={handleMarkAsProcessed}>
              Yes
            </Button>
          </>
        }
        showCloseButton
        resultTitle=""
        resultButtons={null}
        showResult={false}
        resultStatus="none"
      />
    </div>
  );
};
